<script setup lang="ts">
  import { onMounted, onUnmounted, computed, ref } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { useModal } from 'vue-final-modal';
  import { search, type FunctionalActivity } from '@/entities/FunctionalActivity';
  import FunctionalActivitiesTable from './FunctionalActivitiesTable.vue';
  import CreateFunctionalActivityModal from './CreateFunctionalActivityModal.vue';
  import EditFunctionalActivityModal from './EditFunctionalActivityModal.vue';
  import AdminListPageLayout from '../AdminListPageLayout.vue';
  import {
    useAdminListPageSetup,
    createAdminListPageProps,
    useItemSelection,
    useFunctionalActivityDelete,
    useFunctionalActivityCreate,
    useFunctionalActivityEdit,
    useFunctionalActivityClone,
  } from '../../lib';
  import { useFunctionalActivitiesExport } from './useFunctionalActivitiesExport';

  const props = defineProps(createAdminListPageProps());

  const {
    items: activities,
    allItems,
    hasMoreItems,
    loadingMore,
    isShowAllMode,
    isInfiniteScrollMode,
    loading,
    handleSearch,
    initSearch,
    metadata,
    currentPage,
    selectedQuantity,
    onPageChanged,
    handleQuantityChange,
    searchTerm,
    cleanup,
    handleSortOptions,
    isUsingFilters,
    sort,
    order,
    filter,
  } = useAdminListPageSetup<FunctionalActivity>(
    {
      defaultSearchUrl: '/functional-activities',
      searchFunction: search,
      defaultAmount: 10,
      skipShowAllPrefetch: true,
    },
    props
  );

  const { handleExportToExcel } = useFunctionalActivitiesExport({
    searchTerm,
    sort,
    order,
    filter,
  });

  const {
    selectedCount,
    hasSelected,
    isSelected,
    toggleSelection,
    selectedItems,
    deselectAll,
    toggleAll,
  } = useItemSelection<FunctionalActivity>();

  const activitiesList = computed<FunctionalActivity[]>(() =>
    isShowAllMode.value ? (allItems.value ?? []) : (activities.value ?? [])
  );

  const allSelected = computed(
    () =>
      activitiesList.value.length > 0 &&
      activitiesList.value.every((activity: FunctionalActivity) => isSelected(activity.id))
  );

  const handleToggleAll = () => {
    toggleAll(activitiesList.value);
  };

  const autoSelectedIds = ref<number[]>([]);

  const route = useRoute();
  const router = useRouter();

  const refreshList = async () => {
    if (isShowAllMode.value) {
      currentPage.value = 1;
      if (route.query.page !== undefined) {
        const rest = { ...route.query };
        delete rest.page;
        await router.replace({ query: rest });
      }
    }
    await initSearch();
  };

  const { openDeleteConfirmationModal, showNoSelectionError } = useFunctionalActivityDelete(
    () => selectedCount.value,
    () => selectedItems.value,
    async (_deletedIds: number[]) => {
      await refreshList();
    },
    (_ids: number[]) => {
      deselectAll();
    },
    () => {
      autoSelectedIds.value.forEach((id) => {
        if (isSelected(id)) {
          toggleSelection(id);
        }
      });
      autoSelectedIds.value = [];
    }
  );

  const itemsPerPage = computed({
    get: () => selectedQuantity.value,
    set: (val: number) => {
      if (val > 0 && val <= 100) {
        handleQuantityChange(val);
      }
    },
  });

  const handlePageChange = (page: number) => {
    onPageChanged(page);
  };

  const { createFunctionalActivity } = useFunctionalActivityCreate();
  const { updateFunctionalActivity } = useFunctionalActivityEdit();

  const selectedActivity = ref<FunctionalActivity | null>(null);
  const nameError = ref('');

  const createModal = useModal({
    component: CreateFunctionalActivityModal,
    attrs: {
      title: 'Nova Atividade Funcional',
      get nameError() {
        return nameError.value;
      },
      onClose() {
        nameError.value = '';
        createModal.close();
      },
      async onSave(data: { name: string }) {
        const result = await createFunctionalActivity(data.name);
        if (result.success) {
          createModal.close();
          await refreshList();
        } else if (result.nameError) {
          nameError.value = result.nameError;
        }
      },
    },
  });

  const editNameError = ref('');

  const editModal = useModal({
    component: EditFunctionalActivityModal,
    attrs: {
      title: 'Editar Atividade Funcional',
      get activity() {
        return selectedActivity.value;
      },
      get nameError() {
        return editNameError.value;
      },
      onClose() {
        editNameError.value = '';
        editModal.close();
      },
      async onSave(data: { name: string }) {
        if (!selectedActivity.value) return;
        const result = await updateFunctionalActivity(selectedActivity.value.id, data.name);
        if (result.success) {
          editModal.close();
          await refreshList();
        } else if (result.nameError) {
          editNameError.value = result.nameError;
        }
      },
    },
  });

  const { handleClone } = useFunctionalActivityClone({
    activities: activitiesList,
  });

  const handleCreate = () => {
    createModal.open();
  };

  const handleEdit = (id: number) => {
    const activity = activitiesList.value.find((a) => a.id === id);
    if (!activity) return;
    selectedActivity.value = activity;
    editNameError.value = '';
    editModal.open();
  };

  const handleDelete = async (id: number) => {
    deselectAll();
    toggleSelection(id);
    autoSelectedIds.value = [id];
    await openDeleteConfirmationModal();
  };

  onMounted(async () => {
    await initSearch();
  });

  onUnmounted(() => {
    cleanup();
  });
</script>

<template>
  <div data-testid="admin-functional-activities-page">
    <AdminListPageLayout
      v-model:selected-quantity="itemsPerPage"
      title="Atividades Funcionais"
      search-placeholder="Buscar"
      create-button-text="Nova Atividade"
      entity-name="atividade funcional"
      empty-state-message="Nenhuma Atividade cadastrada"
      :items="activitiesList"
      :loading="loading"
      :search-term="searchTerm"
      :metadata="metadata"
      :is-using-filters="isUsingFilters"
      :current-page="currentPage"
      variant="default-pagination"
      @export="handleExportToExcel"
      @page-changed="handlePageChange"
      @search="handleSearch"
      @create="handleCreate"
    >
      <template #table>
        <FunctionalActivitiesTable
          :activities="activitiesList"
          :loading="loading"
          :is-filter-active="false"
          :search-term="searchTerm"
          :selected-count="selectedCount"
          :has-selected="hasSelected"
          :is-selected="isSelected"
          :selected-items="selectedItems"
          :all-selected="allSelected"
          :on-toggle-selection="toggleSelection"
          :on-toggle-all="handleToggleAll"
          :on-open-delete-confirmation="openDeleteConfirmationModal"
          :on-show-no-selection-error="showNoSelectionError"
          @edit="handleEdit"
          @clone="handleClone"
          @delete="handleDelete"
          @update:sort="handleSortOptions"
        />
      </template>
    </AdminListPageLayout>
    <div
      v-if="isInfiniteScrollMode"
      id="infinite-scroll-trigger"
      class="py-4 text-center"
      role="status"
      :aria-live="loadingMore ? 'polite' : 'off'"
      :aria-busy="loadingMore"
    >
      <div
        v-if="loadingMore"
        class="py-2 text-gray-03"
      >
        <span class="inline-block animate-spin mr-2">⟳</span>
        Carregando mais atividades...
      </div>
      <div
        v-else-if="!hasMoreItems && activitiesList.length > 0"
        class="py-2 text-gray-03"
      >
        Não há mais atividades para carregar
      </div>
    </div>
  </div>
</template>
